import React from 'react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
    Globe,
    Check,
    ArrowUpRight,
    FileText,
    Shield,
    Mail,
    HelpCircle,
    CreditCard,
    MessageCircle,
    Trash2,
    AlertTriangle
} from 'lucide-react';

const Settings: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { deleteAccount } = useAuth();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    // Conferma a 2 step: l'utente deve digitare la parola richiesta prima di poter eliminare
    const DELETE_CONFIRM_WORD = 'ELIMINA';
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const canConfirmDelete = deleteConfirmText.trim().toUpperCase() === DELETE_CONFIRM_WORD;

    const closeDeleteModal = () => {
        if (isDeleting) return;
        setIsDeleteModalOpen(false);
        setDeleteConfirmText('');
    };

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    const handleWhatsAppSupport = () => {
        window.open(`https://wa.me/447593288804`, '_blank');
    };

    const handleDeleteAccount = async () => {
        if (!canConfirmDelete) return;
        setIsDeleting(true);
        try {
            const { success, error } = await deleteAccount();
            if (success) {
                toast.success('Account eliminato con successo.');
                setIsDeleteModalOpen(false);
                setDeleteConfirmText('');
            } else {
                toast.error(error || 'Errore durante l\'eliminazione dell\'account');
            }
        } catch (e) {
            toast.error('Si è verificato un errore imprevisto');
        } finally {
            setIsDeleting(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-3xl mx-auto space-y-8 pb-12"
        >
            <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('settings.title')}</h1>
                <p className="text-gray-500 mt-1">{t('settings.subtitle')}</p>
            </div>

            <div className="space-y-6">

                {/* Language Switcher */}
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Globe size={20} className="text-clip-500" /> {t('settings.language.title')}
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">{t('settings.language.desc')}</p>
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => changeLanguage('it')}
                            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all border ${i18n.language === 'it' ? 'bg-clip-500 text-white border-clip-500 shadow-md shadow-clip-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                        >
                            Italiano 🇮🇹
                        </button>
                        <button
                            onClick={() => changeLanguage('en')}
                            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all border ${i18n.language === 'en' ? 'bg-clip-500 text-white border-clip-500 shadow-md shadow-clip-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                        >
                            English 🇺🇸
                        </button>
                    </div>
                </motion.div>

                {/* Payments Integration (Stripe Only) */}
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <CreditCard size={20} className="text-clip-500" /> {t('settings.payments.title')}
                    </h3>

                    <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white p-2 flex items-center justify-center border border-gray-100 shadow-sm">
                                <img src="https://cdn.iconscout.com/icon/free/png-256/free-stripe-2-498440.png" alt="Stripe" className="w-full h-full object-contain" />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900">{t('settings.payments.stripe')}</h3>
                                <p className="text-sm text-gray-500">{t('settings.payments.desc')}</p>
                            </div>
                        </div>

                        {/* Stato Connessione Mockato a TRUE per UI, logica da implementare se necessario */}
                        <button className="w-full sm:w-auto px-4 py-2 bg-green-100 text-green-700 text-sm font-bold rounded-lg border border-green-200 flex items-center justify-center gap-2 cursor-default">
                            <Check size={16} /> {t('settings.payments.connected')}
                        </button>
                    </div>
                </motion.div>

                {/* Legal & Privacy */}
                <motion.div variants={itemVariants} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <Shield size={20} className="text-clip-500" /> {t('settings.legal.title')}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <a
                            href="https://www.clipbooking.com/#/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-clip-200 hover:bg-clip-50 transition-all group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-white transition-colors">
                                    <Shield size={18} className="text-gray-600 group-hover:text-clip-500" />
                                </div>
                                <span className="font-bold text-gray-700 group-hover:text-clip-700">{t('settings.legal.privacy')}</span>
                            </div>
                            <ArrowUpRight size={16} className="text-gray-400 group-hover:text-clip-500" />
                        </a>

                        <a
                            href="https://www.clipbooking.com/#/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-clip-200 hover:bg-clip-50 transition-all group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-white transition-colors">
                                    <FileText size={18} className="text-gray-600 group-hover:text-clip-500" />
                                </div>
                                <span className="font-bold text-gray-700 group-hover:text-clip-700">{t('settings.legal.terms')}</span>
                            </div>
                            <ArrowUpRight size={16} className="text-gray-400 group-hover:text-clip-500" />
                        </a>
                    </div>
                </motion.div>

                {/* Eliminazione account — azione distruttiva resa poco prominente (in fondo, in piccolo) */}
                <motion.div variants={itemVariants} className="pt-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-1">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-500">Eliminazione account</h4>
                            <p className="text-xs text-gray-400 max-w-md">
                                Azione irreversibile: tutti i dati, clienti e appuntamenti verranno cancellati.
                            </p>
                        </div>
                        <button
                            onClick={() => setIsDeleteModalOpen(true)}
                            className="self-start sm:self-auto flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-700 hover:underline transition-colors"
                        >
                            <Trash2 size={14} /> Elimina account
                        </button>
                    </div>
                </motion.div>

                {/* Support & Contact */}
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-3xl shadow-lg text-white">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div>
                            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                                <HelpCircle size={24} className="text-clip-400" /> {t('settings.support.title')}
                            </h3>
                            <p className="text-gray-300 text-sm max-w-md">
                                {t('settings.support.desc')}
                            </p>
                        </div>
                        {/* WhatsApp Button */}
                        <button
                            onClick={handleWhatsAppSupport}
                            className="px-6 py-3 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#20bd5a] transition-colors flex items-center gap-2 shadow-lg"
                        >
                            <MessageCircle size={18} /> Chat WhatsApp
                        </button>
                    </div>
                </motion.div>

                {/* System Info */}
                <div className="text-center pt-4">
                    <p className="text-xs text-gray-400 font-medium">Clip Management System v1.1.0</p>
                </div>
            </div>

            {/* DELETE ACCOUNT MODAL — conferma a 2 step (digitare la parola) */}
            <ConfirmDialog
                isOpen={isDeleteModalOpen}
                onCancel={closeDeleteModal}
                onConfirm={handleDeleteAccount}
                title="Elimina definitivamente l'account?"
                description="Questa azione è permanente e non può essere annullata. Tutti i tuoi dati, clienti e appuntamenti verranno cancellati per sempre."
                confirmLabel={isDeleting ? 'Eliminazione...' : 'Elimina per sempre'}
                cancelLabel="Annulla"
                variant="danger"
                loading={isDeleting}
                confirmDisabled={!canConfirmDelete}
            >
                <div className="text-left">
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                        Per confermare, digita <span className="font-bold text-red-600">{DELETE_CONFIRM_WORD}</span>
                    </label>
                    <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder={DELETE_CONFIRM_WORD}
                        autoFocus
                        disabled={isDeleting}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none disabled:opacity-50"
                    />
                </div>
            </ConfirmDialog>
        </motion.div>
    );
};

export default Settings;